import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDependencies } from '../../../services/scm/Dependencies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faLock, faUnlock, faLink, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import './AddDependencies.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPackageInfo, updatePackage } from '../../../services/scm/Package';
import EllipsedCell from '../../../components/ellipsedCell/EllipsedCell';
import { Skeleton } from '@mui/material';
import { formatToBrowserTime } from '../../../utils/timeFormat';
import { toast } from 'react-toastify';
import { useFeatures } from '../../../utils/featureContext';
import { FEATURES } from '../../../utils/extractFeaturesList.js';

const StatusDisplay = ({ status, pkgLoading, t }) => {
    if (pkgLoading) {
        return <Skeleton width={80} height={20} />;
    }

    const isPublished = status?.toUpperCase() === 'PUBLISHED';
    return (
        <>
            {status || '—'}
            <FontAwesomeIcon
                icon={isPublished ? faLock : faUnlock}
                className={isPublished ? 'text-success' : 'text-danger'}
                title={isPublished ? t('Locked - Published') : t('Editable - Not Published')}
            />
        </>
    );
};

const ForceEditControls = ({ status, hasForceEditPermission, baseEditable, forceEdit, handleForceEdit, t }) => {
    const isPublished = status?.toUpperCase() === 'PUBLISHED';
    
    if (!isPublished || !hasForceEditPermission || baseEditable) {
        return null;
    }

    if (forceEdit) {
        return (
            <span className="badge bg-danger ms-2">
                {t('Force edit active')}
            </span>
        );
    }

    return (
        <button
            type="button"
            className="btn btn-sm btn-outline-danger force-edit-btn"
            onClick={handleForceEdit}
        >
            {t('Force Edit')}
        </button>
    );
};

export default function AddDependencies({ title = 'Select Dependencies' }) {
    const { t } = useTranslation();
    const { features, loading: featuresLoading } = useFeatures();
    const location = useLocation();
    const navigate = useNavigate();

    const navState = (location?.state) || {};

    const pack = navState.packageName;
    const ver = navState.versionName;

    const params = useMemo(() => ({ packageName: pack, versionName: ver }), [pack, ver]);

    const [packageName, setPackageName] = useState('');
    const [version, setVersion] = useState('');
    const [description, setDescription] = useState('');
    const [expiration, setExpiration] = useState('');
    const [assetType, setAssetType] = useState('');
    const [status, setStatus] = useState('');

    const [allGroups, setAllGroups] = useState([]);
    const [depArns, setDepArns] = useState([]);
    const [selected, setSelected] = useState('');
    const [addedGroups, setAddedGroups] = useState([]);

    const [depLoading, setDepLoading] = useState(false);
    const [pkgLoading, setPkgLoading] = useState(false);

    const [forceEdit, setForceEdit] = useState(false);
    const hasForceEditPermission = useMemo(
        () => features?.includes(FEATURES.SCM_ADMIN),
        [features]
    ); // Placeholder for actual permission check

    const baseEditable = (status || '').toUpperCase() !== 'PUBLISHED';

    const isEditable =
        !featuresLoading &&
        (baseEditable || (forceEdit && hasForceEditPermission));

    const handleForceEdit = () => {
        if (!hasForceEditPermission) return;

        const confirmed = window.confirm(
            t('Are you sure you want to force edit dependencies for this PUBLISHED package?')
        );

        if (!confirmed) return;

        setForceEdit(true);
        toast.warn(
            t('Force edit enabled. Be careful when modifying dependencies of a published package.')
        );
    };

    const formatFilters = (filters = {}) => {
        const excludeKeys = Array.isArray(filters.exclude_filters) ? filters.exclude_filters : [];
        const keys = Object.keys(filters).filter(key => key !== 'exclude_filters');

        if (!keys.length) return t('None', '—');

        return keys
            .map(key => {
                const value = filters[key];
                const operator = excludeKeys.includes(key) ? '!=' : '==';
                const formattedValue = Array.isArray(value) ? value.join(', ') : String(value);
                return `${key} ${operator} ${formattedValue}`;
            })
            .join(' & ');
    };

    // Fetch all dependency groups
    const getAllDependencies = async () => {
        try {
            setDepLoading(true);
            const raw = await getDependencies();
            const arr = Array.isArray(raw) ? raw : (raw?.data ?? []);
            const normalized = (arr || [])
                .map(x => ({
                    thingGroupName: x.thingGroupName ?? x.name,
                    thingGroupArn: x.thingGroupArn ?? x.arn ?? x.id,
                    filters: x.filters ?? {}
                }))
                .filter(x => x.thingGroupName && x.thingGroupArn);
            setAllGroups(normalized);
            setDepLoading(false);
        } catch (err) {
            console.error("getAllDependencies error", err)
            setDepLoading(false);
        }
    };

    const getPkgInfo = async () => {
        try {
            if (params.packageName && params.versionName) {
                setPkgLoading(true);
                const res = await getPackageInfo(params);
                const pkg = res.find(
                    item =>
                        item.packageName === params.packageName &&
                        item.version === params.versionName
                );
                setPackageName(pkg?.packageName ?? params.packageName ?? '');
                setVersion(pkg?.version ?? params.versionName ?? '');
                setDescription(pkg?.description ?? '');
                setExpiration(pkg?.expiration
                    ? formatToBrowserTime(pkg?.expiration) || pkg?.expiration
                    : "Never",);
                setAssetType(pkg?.assetType ?? '--');
                setStatus(pkg?.status ?? '');
                setDepArns(pkg?.dependencies.filter(Boolean));
                setPkgLoading(false);
            }
            else {
                toast.warn("No package slected to view/edit dependencies")
                navigate("/software-management/find-package");
            }
        } catch (e) {
            console.error('getPkgInfo error:', e);
            setPkgLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        getAllDependencies();
        getPkgInfo();
    }, [params.packageName, params.versionName]);

    useEffect(() => {
        if (!allGroups.length) return;
        if (!depArns.length) {
            if (addedGroups.length === 0) setAddedGroups([]);
            return;
        }
        if (addedGroups.length === 0) {
            const matched = allGroups.filter(g => depArns.includes(g.thingGroupArn));
            setAddedGroups(matched);
        }
    }, [allGroups, depArns]);

    const onAdd = (groupName) => {
        if (!isEditable) return;
        if (!groupName) return;
        if (addedGroups.some(g => g.thingGroupName === groupName)) return;
        const item = allGroups.find(g => g.thingGroupName === groupName);
        if (item) setAddedGroups(prev => [...prev, item]);
    };

    const removeOne = (name) => {
        if (!isEditable) return;
        setAddedGroups(prev => prev.filter(g => g.thingGroupName !== name));
    };

    const onSave = async () => {
        try {
            const body = {
                packageName: packageName,
                versionName: version,
                dependencyGroups: addedGroups.map(g => g.thingGroupArn)
            };

            await updatePackage(body);

            toast.success(t('Dependencies updated successfully!'));

            navigate('/software-management/package-summary', {
                state: {
                    packageName,
                    versionName: version
                }
            });
        } catch (err) {
            console.error("Update failed:", err);
            toast.error(t('Failed to update dependencies.'));
        }
    };


    return (
        <div className="container my-3">
            <div className="card-body col-12 col-xxl-10 responsive-col">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">{t(title)}</h5>
                </div>

                {/* TOP DETAILS */}
                <div className="card mt-2 bg-light no-border">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-12 col-md-5 d-flex ad-field with-divider">
                                <div className="ad-label">{t('Package Name')}:</div>
                                <div className="ad-value custom-color">{pkgLoading ? <Skeleton width={200} height={20} /> : <EllipsedCell value={packageName} /> || '—'}</div>
                            </div>

                            <div className="col-12 col-md-4 d-flex ad-field with-divider">
                                <div className="ad-label">{t('Version')}:</div>
                                <div className="ad-value custom-color">{pkgLoading ? <Skeleton width={80} height={20} /> : <EllipsedCell value={version} /> || '—'}</div>
                            </div>

                            <div className="col-12 col-md-3 d-flex ad-field">
                                <div className="ad-label">{t('Asset Type')}:</div>
                                <div className="ad-value">{pkgLoading ? <Skeleton width={80} height={20} /> : <EllipsedCell value={assetType} /> || '—'}</div>
                            </div>

                            <div className="col-12 col-md-5 d-flex ad-field with-divider">
                                <div className="ad-label">{t('Description')}:</div>
                                <div className="ad-value">{pkgLoading ? <Skeleton width={200} height={20} /> : <EllipsedCell value={description} /> || '—'}</div>
                            </div>

                            <div className="col-12 col-md-4 d-flex ad-field">
                                <div className="ad-label">{t('Expiration')}:</div>
                                <div className="ad-value">{pkgLoading ? <Skeleton width={80} height={20} /> : <EllipsedCell value={expiration} /> || '—'}</div>
                            </div>

                            <div className="col-12 col-md-3 d-flex ad-field">
                                <div className="ad-label">{t('Status')}:</div>
                                <div className="ad-value">
                                    <StatusDisplay status={status} pkgLoading={pkgLoading} t={t} />
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                {/* SELECTOR */}
                <div className="card mt-3 no-border">
                    <div className="card-body">
                        {/* Top-right FORCE EDIT button */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-start text-muted small mb-2">
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="me-2 mt-1 text-primary"
                                />
                                <span>
                                    {t(
                                        'Deployment applies to assets that belong to any of the selected dependency groups. Assets are not required to be members of all groups. If no groups are selected, the package is eligible for deployment to all assets.'
                                    )}
                                </span>
                            </div>

                            <div className="d-flex align-items-center">
                                <ForceEditControls 
                                    status={status}
                                    hasForceEditPermission={hasForceEditPermission}
                                    baseEditable={baseEditable}
                                    forceEdit={forceEdit}
                                    handleForceEdit={handleForceEdit}
                                    t={t}
                                />
                            </div>
                        </div>

                        {isEditable && <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <select
                                    className="form-select"
                                    value={selected}
                                    disabled={!isEditable}
                                    title={!isEditable ? t('Editing disabled for PUBLISHED') : undefined}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSelected(value);
                                        onAdd(value);
                                        setSelected('');
                                    }}
                                >
                                    <option value="">{t('Select Dependency Groups')}</option>
                                    {allGroups
                                        .filter(g => !addedGroups.some(a => a.thingGroupName === g.thingGroupName))
                                        .map(g => (
                                            <option key={g.thingGroupArn} value={g.thingGroupName}>
                                                {g.thingGroupName}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <div className="col-12 col-md-6 d-flex justify-content-end">
                                <button
                                    className="btn btn-transparent text-primary p-0 d-flex align-items-center gap-2"
                                    disabled={!isEditable}
                                    style={{ textDecoration: isEditable ? 'underline' : 'none' }}
                                    title={!isEditable ? t('Editing disabled for PUBLISHED') : undefined}
                                    onClick={() =>
                                        navigate('/software-management/manage-dependencies', {
                                            state: { packageName, versionName: version }
                                        })
                                    }
                                >
                                    <FontAwesomeIcon icon={faLink} />
                                    {t('Manage Dependencies')}
                                </button>
                            </div>

                        </div>
                        }

                        {/* ADDED LIST */}
                        <div className="mt-4">
                            <h6 className="mb-2">{t('Selected Groups')}</h6>

                            {addedGroups.length === 0 ? (
                                <div className="text-muted">{depLoading ? t("Loading....") : t('No Groups selected')}</div>
                            ) : (
                                <div className="table-responsive ad-table-wrap">
                                    <table className="table table-sm align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '30%' }}>{t('Dependency Group')}</th>
                                                <th style={{ width: '60%' }}>{t('Filters')}</th>
                                                <th style={{ width: '10%' }} className="text-end"></th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {addedGroups.map(g => (
                                                <tr key={g.thingGroupArn}>
                                                    <td className="fw-semibold">{g.thingGroupName}</td>
                                                    <td className="text-muted filters-cell">{formatFilters(g.filters)}</td>
                                                    <td className="text-end">
                                                        {isEditable && (
                                                            <button
                                                                className="btn btn-sm btn-light border-0 text-danger p-1"
                                                                onClick={() => removeOne(g.thingGroupName)}
                                                                title={t('Remove')}
                                                            >
                                                                <FontAwesomeIcon icon={faXmark} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {!isEditable && (
                                <div className="small text-muted mt-1">
                                    {t('Dependencies are view-only when the package is PUBLISHED.')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* ACTION BUTTONS */}
                <div className="mt-3 d-flex justify-content-end gap-2">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => navigate(-1)}
                    >
                        {t('Cancel')}
                    </button>

                    <button className="btn btn-primary" onClick={onSave} disabled={!isEditable}>
                        {t('Save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
